import axios, { CancelTokenSource } from 'axios';
import CheckBox from 'components/final-form/CheckBox';
import Input from 'components/final-form/Input';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Grid } from 'semantic-ui-react';
import { addDiagnosis, editDiagnosis } from 'services/diagnosisServices';
import { UpsertDiagnosisDto } from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { composeValidators, maxChars, required } from 'utils/validatorUtils';

interface Props extends MessageSourceProps {
  toggleAddEditModal: () => void,
  onCancel: () => void,
  onSave: () => void,
  diagnosisId?: number,
  diagnosis?: UpsertDiagnosisDto
}

interface State {
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource
}

class UpsertDiagnosisForm extends Component<Props, State> {

  initialValues: Partial<UpsertDiagnosisDto> = {
    active: true
  };

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      cancelTokenSource
    }

    if (props.diagnosis) {
      this.initialValues = {
        ...props.diagnosis
      };
    }

  }

  handleError = (err: any, values: any) => {
    const {getMessage, getMessageWithNamedParams} = this.props;
    const {data} = err.response;

    let errorMessage = getMessage("error.general");

    if (!!data && !!data.violations) {

      const {violations} = data;

      const violation = violations[0];

      if (violation.errorCode === errorUtils.diagnosisCodeAlreadyExists) {
        errorMessage = getMessageWithNamedParams(`error.${violation.errorCode}`, {code: values.code});
      }
    }

    this.setState({
      errorMessage: errorMessage
    });
  }

  handleSubmit = async (values: Partial<UpsertDiagnosisDto>) => {

    let saveValues: Partial<UpsertDiagnosisDto> = {...values};

    const {diagnosisId, onSave} = this.props;
    const {cancelTokenSource} = this.state;

    if (diagnosisId) {
      await editDiagnosis(diagnosisId, saveValues, cancelTokenSource)
          .then(onSave)
          .catch((err) => this.handleError(err, values))
    } else {
      await addDiagnosis(saveValues, cancelTokenSource)
          .then(onSave)
          .catch((err) => this.handleError(err, values))
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {
    return (
        <FinalForm
            onSubmit={(values) => this.handleSubmit(values)}
            initialValues={this.initialValues}
            subscription={{values: true, pristine: true, submitting: true}}
            render={this.renderForm}
        />
    );
  }

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessage
                  && (
                      <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                        {errorMessage}
                      </StyledErrorMessage>
                  )
              }
              <PraxFormGrid>
                <Grid.Row>
                  <Grid.Column width={5} verticalAlign="middle">
                    <Field
                        fluid
                        name="active"
                        component={CheckBox}
                        label={getMessage("common.active")}
                        toggle
                        validate={composeValidators(maxChars(10), required)}
                        autoFocus
                    />
                  </Grid.Column>
                  <Grid.Column width={5} verticalAlign="middle">
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5} verticalAlign="middle">
                    {getMessage("diagnosis.code")}
                  </Grid.Column>
                  <Grid.Column width={5} verticalAlign="middle">
                    <Field
                        fluid
                        name="code"
                        component={Input}
                        validate={composeValidators(maxChars(10), required)}
                        autoFocus
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("diagnosis.description")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="description"
                        component={Input}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("diagnosis.descriptionEn")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="descriptionEn"
                        component={Input}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("diagnosis.descriptionFr")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="descriptionFr"
                        component={Input}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                  <Grid.Column width={5}>
                    {getMessage("diagnosis.descriptionIt")}
                  </Grid.Column>
                  <Grid.Column width={11}>
                    <Field
                        fluid
                        name="descriptionIt"
                        component={Input}
                        validate={required}
                    />
                  </Grid.Column>
                </Grid.Row>
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="Submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

}

export default withMessages(UpsertDiagnosisForm);
